import { useEffect, useState } from "react";
import { GetMailboxHealth, MailboxHealthCard } from "../CoreMailProcessing/MailboxAPI";
// Shared table, absent-marker, notice and dot classes. Imported explicitly
// rather than relying on Main.tsx's import of CoreMailProcessing to have
// loaded them — this page must not depend on another page's import graph.
import "../CoreMailProcessing/styles.css";
import Loading from "../Generic/loading";
import { parseEmsDate } from "../Logic/emsDates";
import EmailDetail from "./components/EmailDetail";
import EmailTable from "./components/EmailTable";
import FilterBar from "./components/FilterBar";
import Pager from "./components/Pager";
import { EmailRef } from "./EmailSearchAPI";
import "./styles.css";
import { withSort } from "./searchCriteria";
import { SearchState, useEmailSearch } from "./useEmailSearch";
import { useTenantToken } from "./useTenantToken";

export interface iTenantEmailsProps {
    /** The STS roster, from Main.tsx. Only tenants this user can elevate into. */
    tenants: { key: string; name: string }[];
    /** The app-wide selected tenant, whose token needs no fresh mint. */
    defaultTenantKey?: string;
    defaultToken?: string;
    /** Preselected tenant from a drill-down deep link. Consumed once. */
    initialTenantKey?: string;
    /** Preselected mailbox from a drill-down deep link. Consumed once. */
    initialMailboxID?: number;
}

/**
 * Copy for the states that render an explanation rather than content.
 *
 * A `Record`, so a ninth `SearchState` cannot compile until it has copy. Nine
 * sibling `state === "x" &&` conditionals would instead render a header over
 * nothing — indistinguishable from a page mid-load, with every test green.
 */
const NOTICE: Record<Exclude<SearchState, "loading" | "ready">, string> = {
    empty: "No emails matched. Widen the filters, or switch direction — inbound and outbound are separate searches.",
    // NOT an error panel. The picker only lists tenants the STS says this user
    // can elevate into, so this is close to unreachable — but entitlement can
    // be revoked mid-session. The wording must NOT name a single cause: the STS
    // refuses identically whether the account lacks access or the tenant is
    // absent from this environment's configuration, and answering "no such
    // tenant" would leak the tenant list. Naming one cause is wrong some of
    // the time (contract §5).
    notEntitled:
        "This tenant could not be opened. Either your account has no access to it, or it is not configured in " +
        "this environment — the sign-in service refuses both the same way, so this page cannot tell which.",
    // A different sentence on purpose: elevation SUCCEEDED here and the
    // tenant's own API still refused. That indicts a different system.
    forbidden:
        "This tenant's mail-processing endpoints refused the request. Opening the tenant worked, so the block is " +
        "inside that tenant rather than on your access to it.",
    unauthorised: "Your session has expired. Reload the page to sign in again.",
    // A 400 is a CLIENT BUG here, not user error: every control is constrained
    // so a rejection is unreachable. Saying so points the reader at the right
    // team instead of at their own filter choices.
    //
    // The ONLY entry with no terminal punctuation, on purpose — `noticeFor`
    // supplies it. See its doc comment.
    rejected: "The search was rejected. That is a fault in this page rather than in what you selected",
    failed: "The search request failed.",
};

/**
 * The notice sentence, with the punctuation that matches what follows it.
 *
 * `rejected`'s copy introduces the message list, so it wants a colon — but
 * `searchErrorMessages` returns `[]` for a `400` with no body or one whose
 * body could not be read, and then no list is rendered and the colon
 * introduces nothing. The punctuation follows what is actually about to be
 * shown rather than what usually is.
 */
function noticeFor(state: Exclude<SearchState, "loading" | "ready">, hasMessages: boolean): string {
    if (state !== "rejected") {
        return NOTICE[state];
    }
    return hasMessages ? `${NOTICE.rejected}:` : `${NOTICE.rejected}.`;
}

function TenantEmails({
    tenants,
    defaultTenantKey,
    defaultToken,
    initialTenantKey,
    initialMailboxID,
}: iTenantEmailsProps) {
    // Consumed once on mount. Re-applying a stale deep-link target on a later
    // render would silently override whatever the operator had selected.
    const [tenantKey, setTenantKey] = useState(initialTenantKey ?? defaultTenantKey ?? tenants[0]?.key ?? "");
    const [mailboxes, setMailboxes] = useState<MailboxHealthCard[]>([]);

    // Held as state, NOT rebuilt per render. `useEmailDetail`'s effect depends
    // on this object, so a fresh `{direction, id}` literal each render would
    // refetch forever.
    const [openEmail, setOpenEmail] = useState<EmailRef | undefined>(undefined);

    // The same getter useEmailSearch uses, so opening an email mints nothing.
    // It is also the ONE copy of the default-tenant rule left in this file: the
    // mailbox effect below hand-rolled a second one, behind its own ref, for
    // the reason that hook's doc comment now carries — `defaultToken` is
    // `selectedTenant.token`, which Main.tsx's `checkTenantToken` REPLACES on a
    // seven-minute timer, and a rotation should be USED BY the next request
    // rather than CAUSE one. `useTenantToken` is permanently identity-stable,
    // so it can sit in that effect's dependency array and still never re-run it.
    const tokenFor = useTenantToken({ defaultTenantKey, defaultToken });

    const search = useEmailSearch({ tenantKey, defaultTenantKey, defaultToken });

    // Rows carry mailboxID only, so the names come from the mailbox endpoint —
    // which also supplies the mailbox filter's options. One request, two uses.
    useEffect(() => {
        let current = true;

        // Cleared BEFORE the new tenant's list arrives. Mailbox ids are
        // per-tenant, so holding the previous tenant's id-to-name map across a
        // switch does not merely show stale names — it labels this tenant's
        // mailbox 60 with the other tenant's name for 60, which is wrong data
        // rendered confidently. An unnamed "Mailbox 60" is the honest gap.
        setMailboxes([]);

        void (async () => {
            try {
                const token = await tokenFor(tenantKey);
                const response = await GetMailboxHealth(token);
                if (current && response.isOk) {
                    // `?? []` on a COLLECTION: a 200 with no body and one with
                    // [] are the same answer — no mailboxes configured.
                    setMailboxes(response.data ?? []);
                }
            } catch {
                // A failed name lookup must not break the worklist. The table
                // falls back to "Mailbox <id>" and the filter offers nothing,
                // which is degraded but honest — unlike an error panel over a
                // search that actually succeeded.
                if (current) {
                    setMailboxes([]);
                }
            }
        })();

        return () => {
            current = false;
        };
    }, [tenantKey, tokenFor]);

    useEffect(() => {
        if (initialMailboxID !== undefined) {
            search.setFilters({ ...search.filters, mailboxID: initialMailboxID });
        }
        // Deliberately mount-only: a deep link preselects once, and re-running
        // this would fight the operator's own mailbox choice on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // `asOf` is stamped per request and DOES carry an offset, unlike the
    // database-sourced timestamps in the rows (contract §1.3). `parseEmsDate`
    // passes an offset-bearing value straight through, and returns undefined
    // for an absent or unparseable one rather than an Invalid Date.
    const asOfClock = parseEmsDate(search.asOf)?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    const names = new Map<number, string>();
    for (const card of mailboxes) {
        if (card.name) {
            names.set(card.mailboxID, card.name);
        }
    }

    /*
        The worklist subtree unmounts while an email is open, and that is safe:
        `useEmailSearch` is called HERE, in a component that stays mounted, so
        the filters, page and rows survive with no refetch. `CoreMailProcessing`
        does exactly this for its tenant drill-down and pins the invariant in
        its own index test; this file now pins the same one.

        If `useEmailSearch` is ever moved down into a child, this stops being
        true and every drill-down costs a full search round trip.

        `staleWorklist` is the one deliberate difference from
        `CoreMailProcessing`, which never refetches on the way back. This is the
        only drill-down in the app that CHANGES what the parent list displays: a
        reprocessed email still reading `Failed` in the row behind it is a lie
        the `as of` stamp does not excuse. When nothing was written, nothing is
        refetched.
    */
    if (openEmail) {
        return (
            <EmailDetail
                emailRef={openEmail}
                tenantKey={tenantKey}
                mailboxNames={names}
                tokenFor={tokenFor}
                onBack={staleWorklist => {
                    setOpenEmail(undefined);
                    if (staleWorklist) {
                        search.refresh();
                    }
                }}
            />
        );
    }

    return (
        /*
            A COLUMN that fills `.bodyContainer`, not a bare fragment.

            `#root` is fixed at 100vh with `overflow: hidden` and
            `.bodyContainer` is the app's one scrolling region, so a long
            worklist used to make the whole page tall — which pushed the
            table's own horizontal scrollbar off the bottom of the screen.
            Reaching it meant scrolling to the end of the rows first, and with
            a full page of results the wide outbound columns were effectively
            unreachable.

            Here the page is exactly the height of its container and the table
            scrolls INSIDE it, so both scrollbars stay on screen whatever the
            row count. See `.mp-emails-page` in styles.css.
        */
        <div className="mp-emails-page">
            <div className="topSection">
                <div className="sectionHeader">Mail Processing — Emails</div>
                {/*
                    WRAPPED, and it must stay wrapped. `.topSection :nth-child(2)`
                    in Main/index.css is a DESCENDANT selector giving the header's
                    second child `flex: 0 0 300px`, its own padding and right
                    alignment. That rule is written for a CONTAINER of controls.
                    An unwrapped control does not sit inside that box — it BECOMES
                    it: 300 x 47px, stretched to the header's full height.
                */}
                <div className="mp-head-controls">
                    {/*
                        `refreshing` and `state === "loading"` are mutually
                        exclusive by the hook's own construction, so this cannot
                        read "Refreshing…" on a page that has never loaded.
                    */}
                    <button
                        className="g-btn btn-default"
                        onClick={() => search.refresh()}
                        disabled={search.refreshing}
                    >
                        {search.refreshing ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
            </div>

            <div className="mp-detail-meta">
                <span className="mp-detail-key">{tenantKey}</span>
                {/*
                    Driven by the switch, not fixed copy. The default is
                    `failedOnly` — a FILTER, sorted by last activity descending;
                    nothing is ordered ahead of anything, so "Failures first"
                    claimed an ordering the page does not do. Worse, it stayed
                    on screen verbatim once the operator cleared the switch,
                    where it was simply untrue. This line says what is on screen
                    in either state and claims no ordering at all.
                */}
                <span>
                    {search.filters.failedOnly
                        ? "Showing failures only — this page opens on what needs attention."
                        : "Showing every email matching the filters."}
                </span>
                {/*
                    The freshness stamp, in the same slot and with the same
                    wording as the sibling pages (CoreMailProcessing's header
                    and TenantDetail's meta line), and gated on "ready" the way
                    TenantDetail's is.

                    This page needs it more than either of them: a refresh here
                    deliberately KEEPS the previous rows on screen while the new
                    request runs, so "when was this read" is the one thing that
                    tells an operator whether what they are looking at is
                    current.

                    Nothing is rendered when there is no clock to render.
                    `Paged.asOf` is declared required, so the reachable version
                    of "no stamp" is one that will not parse — `parseEmsDate`
                    answers undefined for that rather than an Invalid Date, and
                    a fallback here would fabricate a reading of exactly the
                    fact this line exists to report.
                */}
                {search.state === "ready" && asOfClock && <span className="mp-asof">as of {asOfClock}</span>}
            </div>

            {/*
                The tenant picker lives HERE rather than in the header beside
                Refresh. It answers the same kind of question as the mailbox
                filter — which slice of mail is on screen — and the two read as
                a pair when they sit together. `tenantKey` is still this
                component's state: FilterBar reports it through its own
                callback, because a tenant switch resets the page and re-reads
                the mailbox list, which a filter change must not do.
            */}
            <FilterBar
                filters={search.filters}
                mailboxes={mailboxes.map(card => ({ mailboxID: card.mailboxID, name: card.name }))}
                tenants={tenants}
                tenantKey={tenantKey}
                onTenantChange={setTenantKey}
                onChange={search.setFilters}
            />

            {search.state === "loading" && <Loading></Loading>}

            {search.state !== "loading" && search.state !== "ready" && (
                <div className="mp-notice">
                    <p>{noticeFor(search.state, search.errors.length > 0)}</p>
                    {search.errors.length > 0 && (
                        <ul className="mp-notice-detail">
                            {/*
                                Keyed on the INDEX as well as the text.
                                `searchErrorMessages` flattens every field into
                                one list, and a 400 routinely repeats the same
                                sentence across two fields ("The field is
                                required."), so the message alone is not unique.
                                React does not drop the duplicate on a first
                                render, it warns and calls the behaviour
                                unsupported — which is a promise about the NEXT
                                render of this list, not a bug we can see today.
                            */}
                            {search.errors.map((message, index) => (
                                <li key={`${index}-${message}`}>{message}</li>
                            ))}
                        </ul>
                    )}
                    {/*
                        `failed` ONLY, which is every state a retry can help.

                        `rejected` is a 400 over criteria this page built, and
                        a retry reissues them byte for byte to be rejected
                        identically — the notice's own copy says the fault is
                        in this page. `notEntitled` and `forbidden` are
                        decisions the STS and the tenant's API make about who
                        is asking, not transient faults, so the answer does not
                        change on a second ask. `unauthorised` needs a fresh
                        sign-in, which its copy asks for and this button cannot
                        do. Only `failed` — a network blip or a 5xx — has any
                        chance of a different answer. TenantDetail draws the
                        line in exactly the same place.
                    */}
                    {search.state === "failed" && (
                        <button
                            className="g-btn btn-default"
                            onClick={() => search.refresh()}
                            disabled={search.refreshing}
                        >
                            {search.refreshing ? "Retrying…" : "Retry"}
                        </button>
                    )}
                </div>
            )}

            {/*
                GATED ON `state`, never on `items.length`. A failed refresh
                deliberately KEEPS the previous rows so an operator watching a
                queue drain does not lose the table on a blip — which makes it
                this view's job not to show them. Rows under a failure banner,
                with nothing marking them stale, read as the current answer.
                `"ready"` with no rows is unreachable (the hook reports `"empty"`
                instead), so no length check is needed here either.

                `itemsDirection`, never `filters.direction`: the switch flips
                immediately while `items` still holds the previous direction's
                rows, and EmailTable takes its header from this prop and its
                cells from each row.
            */}
            {search.state === "ready" && (
                <>
                    {/*
                        The sort state comes from `filters`, NOT from a
                        companion to `itemsDirection`. Direction needs one
                        because it changes the COLUMN COUNT — header and body
                        would structurally disagree for the length of a
                        request. Sorting changes only the row order, and a
                        marker briefly ahead of the rows is what every other
                        control on this page already does: the status select
                        reads the new status while the old rows are still up,
                        and the `as of` stamp is what reports freshness.
                    */}
                    <EmailTable
                        direction={search.itemsDirection}
                        items={search.items}
                        mailboxNames={names}
                        sortBy={search.filters.sortBy}
                        sortAscending={search.filters.sortAscending}
                        // Through `setFilters`, which resets to page 1 — page 4
                        // of one ordering holds different emails from page 4 of
                        // another, so keeping the number strands the operator
                        // mid-list.
                        onSort={column => search.setFilters(withSort(search.filters, column))}
                        onOpen={setOpenEmail}
                    />
                    <Pager
                        page={search.page}
                        itemsPerPage={search.filters.itemsPerPage}
                        totalCount={search.totalCount}
                        onPage={search.setPage}
                    />
                </>
            )}
        </div>
    );
}

export default TenantEmails;
